import React, { useState } from 'react';
import Card from 'components/inputs/Card';
import TextInput from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import Button from 'components/inputs/Button';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Row, Col } from 'react-bootstrap';
import { API } from 'api';
import { toast } from 'react-toastify';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import { Spinner } from 'react-bootstrap';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const CreateAdminAccess = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const initialValues = {
    name: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    role: '',
    country_code: '91',
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required('Full Name is required')
      .max(100, 'Name must be at most 100 characters'),
    username: Yup.string()
      .trim()
      .required('Username is required')
      .min(8, 'Username must be at least 8 characters')
      .max(50, 'Username must be at most 50 characters'),
    email: Yup.string()
      .trim()
      .email('Invalid email address')
      .required('Email address is required'),
    phone: Yup.string()
      .trim()
      .matches(/^[0-9]+$/, 'Phone number must contain only numbers')
      .min(8, 'Phone number must be at least 8 digits')
      .max(15, 'Phone number must be at most 15 digits')
      .required('Phone number is required'),
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters'),
    role: Yup.string()
      .required('Role is required'),
    country_code: Yup.string()
      .trim()
      .matches(/^[0-9]+$/, 'Country code must contain only numbers')
      .required('Country code is required'),
  });

  const onSubmit = async (values, { resetForm }) => {
    if (!values.username || values.username.trim().length < 8) {
      toast.error('Username must be at least 8 characters');
      return;
    }
    setIsLoading(true);
    try {
      const response = await API.createAdminAccess({
        ...values,
        country_code: parseInt(values.country_code, 10),
      });

      if (response && (response.status === 201 || response.status === 200 || response.data?.message)) {
        toast.success(response.data?.message || 'Admin access created successfully');
        resetForm();
      } else {
        toast.error('Failed to create admin access');
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || error?.message || 'Error occurred';
      toast.error(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const roleOptions = [
    { value: 'ZMT_admin', label: 'ZMT_admin' },
    { value: 'support_admin', label: 'support_admin' },
    { value: 'support', label: 'support' },
    { value: 'network_admin', label: 'network_admin' },
    { value: 'finance_admin', label: 'finance_admin' },
    { value: 'finance', label: 'finance' },
    { value: 'md_admin', label: 'md_admin' },
    { value: 'Admin_fleet', label: 'Admin_fleet' },
  ];

  return (
    <LayoutContainer>
      <AdminHeader title="Create Admin Access" />
      <div className="page-content-wrapper scrollable" style={{ padding: '24px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <Card>
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px' }}>
                  Create Admin Access Credentials
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#8898aa', margin: 0 }}>
                  Fill out the details below to provision access for administrative roles. All fields marked with * are required.
                </p>
              </div>

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {({ values, errors, touched, handleSubmit, handleChange, setFieldValue, isSubmitting }) => (
                  <Form
                    onSubmit={(e) => {
                      if (values.username && values.username.trim().length < 8) {
                        toast.error('Username must be at least 8 characters');
                      }
                      handleSubmit(e);
                    }}
                  >
                    <Row>
                      <Col md={6} sm={12}>
                        <TextInput
                          isRequired
                          label="Full Name"
                          placeholder="Enter full name"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          error={errors.name && touched.name ? errors.name : null}
                        />
                      </Col>
                      <Col md={6} sm={12}>
                        <TextInput
                          isRequired
                          label="Username"
                          placeholder="Enter username"
                          name="username"
                          value={values.username}
                          onChange={handleChange}
                          error={errors.username && touched.username ? errors.username : null}
                        />
                      </Col>
                    </Row>

                    <Row style={{ marginTop: '16px' }}>
                      <Col md={6} sm={12}>
                        <TextInput
                          isRequired
                          label="Email Address"
                          placeholder="Enter email address"
                          name="email"
                          type="email"
                          value={values.email}
                          onChange={handleChange}
                          error={errors.email && touched.email ? errors.email : null}
                        />
                      </Col>
                      <Col md={6} sm={12}>
                        <Select
                          isRequired
                          label="Role"
                          placeholder="Select Role"
                          name="role"
                          value={values.role}
                          onChange={(val) => setFieldValue('role', val)}
                          options={roleOptions}
                          error={errors.role && touched.role ? errors.role : null}
                        />
                      </Col>
                    </Row>

                    <Row style={{ marginTop: '16px' }}>
                      <Col md={4} sm={12}>
                        <TextInput
                          isRequired
                          label="Country Code"
                          placeholder="e.g. 91"
                          name="country_code"
                          value={values.country_code}
                          onChange={handleChange}
                          error={errors.country_code && touched.country_code ? errors.country_code : null}
                        />
                      </Col>
                      <Col md={8} sm={12}>
                        <TextInput
                          isRequired
                          label="Phone Number"
                          placeholder="Enter phone number"
                          name="phone"
                          value={values.phone}
                          onChange={handleChange}
                          error={errors.phone && touched.phone ? errors.phone : null}
                        />
                      </Col>
                    </Row>

                    <Row style={{ marginTop: '16px' }}>
                      <Col md={12} sm={12} className="password_div resetpass">
                        <TextInput
                          isRequired
                          label="Password"
                          placeholder="Enter password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          value={values.password}
                          onChange={handleChange}
                          error={errors.password && touched.password ? errors.password : null}
                        />
                        <div
                          type="button"
                          className="password-toggle-button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            cursor: 'pointer',
                            transition: 'color 0.3s ease',
                            color: showPassword ? 'white' : '#be210b',
                          }}
                        >
                          {showPassword ? <FiEye /> : <FiEyeOff />}
                        </div>
                      </Col>
                    </Row>

                    <div className="form-btn__block" style={{ marginTop: '32px', display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
                      <Button
                        type="submit"
                        disabled={isSubmitting || isLoading}
                        className="form_btn"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                      >
                        {(isSubmitting || isLoading) && <Spinner size="sm" animation="border" role="status" />}
                        Create Access
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </Card>
        </div>
      </div>
    </LayoutContainer>
  );
};

export default CreateAdminAccess;
